'use client';

import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { Stack, Theme, Typography } from '@mui/material';
import { ReactElement, useId } from 'react';
import NameValueDisplay from '../NameValueDisplay';
import { TBiometricConfidenceLevel, TLivenessDetermination } from './types';

export type TEvidenceFindingsProps = {
  /**
   * The face-match similarity score. A plain prop rather than something resolved from the level:
   * the level a match has to clear is per-organisation rules configuration, so this component has
   * no business deriving a score from it. Omit where no comparison has been made.
   */
  similarityScore?: number;
  /** The liveness result. Resolved to its display wording here, not passed in pre-formatted. */
  liveness: TLivenessDetermination;
};

/** Missing values render as a dash. */
const MISSING_VALUE = '-';

/**
 * How each liveness result reads in the findings panel.
 *
 * `Partial`, not a full `Record`, even though every current member of `TLivenessDetermination` is
 * listed below. The type is exhaustive at compile time, which does nothing for a value that
 * arrives from an API before the type catches up: a fourth liveness value would look up as
 * `undefined` here, and `Partial` is what makes that `undefined` a value the compiler makes the
 * call site handle, rather than one it types away.
 */
const LIVENESS_DETERMINATION_LABELS: Partial<Record<TLivenessDetermination, string>> = {
  CONFIRMED: 'Confirmed',
  CANNOT_CONFIRM: 'Cannot confirm',
  NO_RESULT: 'No result',
};

/**
 * The evidence dialog's "AUTOMATED FINDINGS" panel: the similarity score and the liveness
 * determination, and nothing else.
 *
 * Takes the liveness result as its enum value, not a pre-formatted label, and resolves its own
 * wording via `LIVENESS_DETERMINATION_LABELS`. That keeps the level-to-wording mapping in one place
 * rather than asking every caller to know how `CANNOT_CONFIRM` should read on screen, the same
 * reason `biometricConfidenceLevelName` exists instead of every card spelling out its own strings.
 *
 * **Takes no `data-testid`.** It is a panel inside the evidence dialog, which carries the
 * page-unique id, so ADR 72 puts it on the hierarchy side. It is named instead: the panel is a
 * `section` labelled by its own heading, so a test reaches it with
 * `getByRole('region', { name: 'Automated findings' })` rather than by matching raw text, which
 * ADR 71 lists among the fragile selectors these attributes exist to replace.
 */
export default function EvidenceFindings({
  similarityScore,
  liveness,
}: Readonly<TEvidenceFindingsProps>): ReactElement {
  const headingId = useId();

  return (
    <Stack
      component="section"
      aria-labelledby={headingId}
      sx={(t: Theme) => ({
        gap: t.semantic.spacing.xs,
        padding: t.semantic.spacing.sm,
        border: `${t.semantic.spacing['4xs']} solid ${t.palette.divider}`,
        borderRadius: t.semantic.borderRadius.sm,
        backgroundColor: t.palette.background.default,
      })}
    >
      <Typography
        id={headingId}
        variant="caption"
        sx={(t: Theme) => ({
          textTransform: 'uppercase',
          letterSpacing: t.semantic.letterSpacing.lg,
          fontWeight: t.semantic.fontWeight.semibold,
          color: 'text.secondary',
        })}
      >
        Automated findings
      </Typography>
      <Stack direction="row" flexWrap="wrap" sx={(t: Theme) => ({ gap: t.semantic.spacing.xl })}>
        <NameValueDisplay
          name="Similarity score"
          value={similarityScore === undefined ? MISSING_VALUE : `${similarityScore}%`}
        />
        <NameValueDisplay
          name="Liveness determination"
          value={LIVENESS_DETERMINATION_LABELS[liveness] ?? MISSING_VALUE}
        />
      </Stack>
    </Stack>
  );
}

/**
 * Which input is behind a biometric outcome, in a sentence.
 *
 * This is the whole justification for `View evidence` being the biometric card's only primary
 * action: from the card a client sees a level, not what produced it. Opening the evidence names
 * the shortfall for the two levels that have one: `Low` states whether liveness was confirmed,
 * and what that means for the outcome, and `InProgress` names the missing ID portrait.
 *
 * `Low` with `CANNOT_CONFIRM` names no similarity score: it is also reached with no comparison, where
 * the findings panel shows a dash, so the sentence holds with or without one.
 *
 * **`InProgress` always reads that way, regardless of which capture is actually absent.** This
 * function takes only `level` and `liveness`, with no capture-presence input, so it cannot tell a
 * missing face check from a missing ID portrait; it names the ID portrait either way. Give it a
 * capture-presence signal before making it distinguish the two, rather than letting the comment
 * claim a distinction the code does not make.
 *
 * Deliberately says nothing about a threshold value. The similarity level a match has to clear is
 * per-organisation rules configuration, so any number printed here would be wrong somewhere.
 *
 * Exported (rather than kept private to this module) so it can be unit tested directly: it is pure
 * logic with a small, enumerable input space, exactly the kind of thing ADR-104 asks to be covered
 * by a Vitest test rather than only exercised through a rendered component.
 *
 * Backed by a nested `Record`, not an if/else chain. A `Record` keyed by both axes forces every
 * `level`/`liveness` pair to be listed: adding a fourth `TLivenessDetermination` value without
 * updating the table below is a compile error, not a silent fall-through to the wrong reason the
 * old if/else chain's implicit final branch would have produced.
 */
const OUTCOME_REASONS: Record<
  TBiometricConfidenceLevel,
  Record<TLivenessDetermination, string | undefined>
> = {
  NotCompleted: { CONFIRMED: undefined, CANNOT_CONFIRM: undefined, NO_RESULT: undefined },
  InProgress: {
    CONFIRMED:
      'The ID portrait has not been attached to this check yet, so no comparison has been made.',
    CANNOT_CONFIRM:
      'The ID portrait has not been attached to this check yet, so no comparison has been made.',
    NO_RESULT:
      'The ID portrait has not been attached to this check yet, so no comparison has been made.',
  },
  Low: {
    CONFIRMED:
      'Liveness was confirmed. The two captures are not similar enough to support a high level of confidence.',
    CANNOT_CONFIRM:
      'Liveness could not be confirmed for this face check. This means the check cannot support a high level of confidence, regardless of whether a photo comparison result is available.',
    NO_RESULT:
      'The two captures are a close match, but no liveness result was returned for this face check.',
  },
  High: { CONFIRMED: undefined, CANNOT_CONFIRM: undefined, NO_RESULT: undefined },
};

export function resolveOutcomeReason(
  level: TBiometricConfidenceLevel,
  liveness: TLivenessDetermination,
): string | undefined {
  return OUTCOME_REASONS[level][liveness];
}

export type TOutcomeReasonProps = {
  level: TBiometricConfidenceLevel;
  liveness: TLivenessDetermination;
};

/**
 * A short callout naming which input produced the outcome, or nothing at all where there is no
 * reason to give.
 *
 * Semantic rather than pre-resolved: it takes the level and the liveness result and owns the
 * mapping via `resolveOutcomeReason`, rather than taking a `reason` string a caller has already
 * worked out. A caller cannot get the wording wrong or forget the `NotCompleted`/`High` cases that
 * render nothing, because those cases are decided in one place, here.
 *
 * **Takes no `data-testid`**, like the findings panel beside it. It carries no heading to label a
 * region with, so it is reached through the dialog by the sentence it renders, which is the whole
 * of its content and is what any assertion about it would be checking anyway.
 */
export function OutcomeReason({
  level,
  liveness,
}: Readonly<TOutcomeReasonProps>): ReactElement | null {
  const reason = resolveOutcomeReason(level, liveness);
  if (!reason) {
    return null;
  }

  return (
    <Stack
      direction="row"
      sx={(t: Theme) => ({
        gap: t.semantic.spacing.xs,
        padding: t.semantic.spacing.sm,
        borderRadius: t.semantic.borderRadius.sm,
        backgroundColor: t.palette.action.hover,
      })}
    >
      <InfoOutlinedIcon fontSize="small" sx={{ color: 'action.active', flex: 'none' }} />
      <Typography variant="body2" sx={{ textWrap: 'pretty' }}>
        {reason}
      </Typography>
    </Stack>
  );
}
